/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import type { ChannelResource, EnvironmentV2Resource, FeedResource, GetTeamsLookupBffResponseTeam, PackageReference, ProjectSummaryResource, PropertyValueResource, WorkerPoolResource } from "@octopusdeploy/octopus-server-client";
import { ControlType } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { roleSummary } from "~/areas/projects/components/Process/Common/TargetRolesFormSection";
import { channelChipList, ChipIcon, ContextualMissingChip, environmentV2ChipList, TeamChip } from "~/components/Chips/index";
import LookupResourceChip from "~/components/LookupResourceChip/index";
import InternalLink from "~/components/Navigation/InternalLink/index";
import TenantTagsList from "~/components/TenantTagsList/TenantTagsList";
import isBound from "~/components/form/BoundField/isBound";
import { default as Summary } from "~/components/form/Sections/Summary";
import type { Options } from "~/primitiveComponents/form/Select/Options";
import selectOptionsToItems from "~/primitiveComponents/form/Select/Options";
import { JsonUtils } from "~/utils/jsonUtils";
import ExpandableFormSection from "../form/Sections/ExpandableFormSection";
import type { ActionTemplateParameterInputProps } from "./ActionTemplateParameterInput";
import ActionTemplateParameterInput, { isProjectSourceItems } from "./ActionTemplateParameterInput";
import { getHelpText, getLabel } from "./ActionTemplateParameterInputRenderer";
type HelpTextMarkupProvider = (label: string) => string;
export default class ActionTemplateParameterInputExpandableFormElement extends React.Component<ActionTemplateParameterInputProps & {
    isExpandedByDefault?: boolean;
    customHelpText?: HelpTextMarkupProvider;
}, never> {
    render() {
        const { isExpandedByDefault, customHelpText, ...inputProps } = this.props;
        const parameter = inputProps.parameter;
        const options = selectOptionsToItems(parameter.DisplaySettings["Octopus.SelectOptions"]);
        const packages = isProjectSourceItems(inputProps.sourceItems) ? inputProps.sourceItems.packages : inputProps.sourceItems;
        const feeds = packages?.feeds ?? [];
        const environments = isProjectSourceItems(inputProps.sourceItems) ? inputProps.sourceItems.environments : [];
        const channels = isProjectSourceItems(inputProps.sourceItems) ? inputProps.sourceItems.channels : [];
        const workerPools: WorkerPoolResource[] = []; // Worker pools are an excluded ControlType
        const teams: GetTeamsLookupBffResponseTeam[] = []; // Teams are an excluded ControlType
        const projects: ProjectSummaryResource[] = []; // Projects are an excluded ControlType
        const type = parameter.DisplaySettings["Octopus.ControlType"];
        const packageParameterIsUsed = packages ? !!packages.items.find((p) => p.Properties["PackageParameterName"] === parameter.Name) : false;
        const label = getLabel(parameter);
        return (<ExpandableFormSection errorKey={parameter.Name} title={label} isExpandedByDefault={isExpandedByDefault} help={getHelpText(parameter, customHelpText, packageParameterIsUsed)} summary={createSummaryFor(label, type!, options, feeds, environments, workerPools, teams, channels, projects, packageParameterIsUsed)(this.props.value)}>
                <ActionTemplateParameterInput {...inputProps}/>
            </ExpandableFormSection>);
    }
    static displayName = "ActionTemplateParameterInputExpandableFormElement";
}
export function createSummaryFor(label: string, type: ControlType, options: Options, feeds: FeedResource[], environments: EnvironmentV2Resource[], workerPools: WorkerPoolResource[], teams: GetTeamsLookupBffResponseTeam[], channels: ChannelResource[], projects: ProjectSummaryResource[], packageParameterIsUsed?: boolean) {
    return (value: PropertyValueResource | undefined) => {
        if (type === ControlType.Package && !packageParameterIsUsed) {
            return Summary.placeholder("This parameter is not used by the step");
        }
        if (value === undefined || value === null) {
            return Summary.placeholder("Provide " + label);
        }
        if (typeof value === "object") {
            return Summary.summary("********");
        }
        if (type === ControlType.Select) {
            const option = options.find((o) => o.value === value);
            if (option) {
                return Summary.summary(option.text);
            }
        }
        if (type === ControlType.Package) {
            if (JsonUtils.tryParseJson(value)) {
                const pkg = JSON.parse(value) as Partial<PackageReference<any>>;
                if (!pkg.PackageId) {
                    return Summary.placeholder("Provide " + label);
                }
                const feed = feeds.find((f) => f.Id === pkg.FeedId);
                return Summary.summary(<span>
                        Package <strong>{pkg.PackageId}</strong>
                        <span>
                            &nbsp;from feed <strong>{!!feed ? feed.Name : pkg.FeedId}</strong>
                        </span>
                    </span>);
            }
        }
        if (type === ControlType.TargetTags) {
            return roleSummary(value);
        }
        if (type === ControlType.WorkerPool) {
            const workerPool = workerPools.find((f) => f.Id === value);
            if (workerPool?.Name) {
                return Summary.summary(workerPool.Name);
            }
        }
        if (type === ControlType.Feed) {
            const feed = feeds.find((f) => f.Id === value);
            if (feed?.Name) {
                return Summary.summary(feed.Name);
            }
        }
        if (type === ControlType.Environments) {
            const selectedEnvironments = value ? value.split(",") : [];
            if (selectedEnvironments.length > 0) {
                return Summary.summary(environmentV2ChipList(environments, selectedEnvironments));
            }
        }
        if (type === ControlType.Teams) {
            const selectedTeams = value ? value.split(",") : [];
            if (selectedTeams.length > 0) {
                const LookupTeamChip = LookupResourceChip<GetTeamsLookupBffResponseTeam>();
                const chips = selectedTeams.map((t) => <LookupTeamChip lookupCollection={teams} key={t} lookupId={t} type={ChipIcon.Environment} chipRender={(team) => <TeamChip team={team}/>}/>);
                return Summary.summary(<div>{chips}</div>);
            }
        }
        if (type === ControlType.TenantTags) {
            const selectedTenantTags = value ? value.split(",") : [];
            if (selectedTenantTags.length > 0) {
                return Summary.summary(<span>
                        This step will only run for tenants tagged with <TenantTagsList tags={selectedTenantTags}/>
                    </span>);
            }
        }
        if (type === ControlType.Channels) {
            const selectedChannels = value ? value.split(",") : [];
            if (selectedChannels.length > 0) {
                return Summary.summary(channelChipList(channels, selectedChannels));
            }
        }
        if (type === ControlType.Project) {
            const project = projects.find((p) => p.Id === value);
            const link = project ? (isBound(value) ? (<b>{value}</b> // Don't link to bound fields
            ) : (<InternalLink to={links.projectRootRedirect.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug })}>{project.Name}</InternalLink>)) : (<ContextualMissingChip lookupKey={value} type={ChipIcon.Project}/>);
            return Summary.summary(<span>{link}</span>);
        }
        return Summary.summary(value);
    };
}
